import { useMemo, useState } from 'react'

type RecordRow = Record<string, unknown>

type Contact = {
  id: number | string
  codigo: string
  ordemSetor: number
  setor: string
  papel: string
  nome: string
  email: string
  telefone: string
}

type Props = {
  records: RecordRow[]
  source: 'online' | 'cache'
  savedAt: string | null
  loading: boolean
  error: string
}

const ROLE_ORDER = ['Gerência', 'Coordenador', 'Supervisão/Líder', 'Analista/Assistente']
const SECTOR_ROLE_ORDER = ['Coordenador', 'Supervisão/Líder', 'Analista/Assistente']
const MANAGER_CODE = 'ACS-040'
const MANAGER_NAME = 'Daniel dos Santos Moreira'

function text(value: unknown) {
  return value == null ? '' : String(value).trim()
}

function normalize(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function isConfirmedManager(codigo: string, nome: string) {
  return codigo === MANAGER_CODE && normalize(nome) === normalize(MANAGER_NAME)
}

function toContact(row: RecordRow, index: number): Contact {
  const codigo = text(row.codigo)
  const nome = text(row.nome) || 'Nome não informado'
  const originalRole = text(row.papel) || 'Responsabilidade não informada'

  return {
    id: (row.id as number | string | undefined) ?? index,
    codigo,
    ordemSetor: Number(row.ordem_setor) || 0,
    setor: text(row.setor) || 'Setor não informado',
    papel: isConfirmedManager(codigo, nome) ? 'Gerência' : originalRole,
    nome,
    email: text(row.email),
    telefone: text(row.telefone),
  }
}

function contactSearchText(contact: Contact) {
  return normalize([contact.codigo, contact.setor, contact.papel, contact.nome, contact.email, contact.telefone].join(' '))
}

function phoneHref(phone: string) {
  const cleaned = phone.replace(/[^\d+]/g, '')
  return cleaned ? `tel:${cleaned}` : ''
}

async function copyText(value: string) {
  if (!value) return false
  try {
    await navigator.clipboard.writeText(value)
    return true
  } catch {
    return false
  }
}

export function AcionamentosCentral({ records, source, savedAt, loading, error }: Props) {
  const [query, setQuery] = useState('')
  const [role, setRole] = useState('todas')
  const [message, setMessage] = useState('')

  const contacts = useMemo(() => records.map(toContact), [records])
  const managers = useMemo(() => contacts.filter((item) => item.papel === 'Gerência'), [contacts])
  const sectorContacts = useMemo(() => contacts.filter((item) => item.papel !== 'Gerência'), [contacts])

  const roles = useMemo(() => {
    const found = Array.from(new Set(contacts.map((item) => item.papel).filter(Boolean)))
    return found.sort((a, b) => {
      const ai = ROLE_ORDER.indexOf(a)
      const bi = ROLE_ORDER.indexOf(b)
      if (ai >= 0 || bi >= 0) return (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi)
      return a.localeCompare(b, 'pt-BR')
    })
  }, [contacts])

  const needle = normalize(query)

  const visibleManagers = useMemo(() => {
    if (role !== 'todas' && role !== 'Gerência') return []
    if (!needle) return managers
    return managers.filter((contact) => contactSearchText(contact).includes(needle))
  }, [managers, needle, role])

  const sectors = useMemo(() => {
    const grouped = new Map<string, Contact[]>()
    sectorContacts.forEach((contact) => {
      const list = grouped.get(contact.setor) ?? []
      list.push(contact)
      grouped.set(contact.setor, list)
    })

    if (role === 'Gerência') return []

    return Array.from(grouped.entries())
      .map(([setor, all]) => {
        const sectorMatchesQuery = !needle || all.some((contact) => contactSearchText(contact).includes(needle))
        if (!sectorMatchesQuery) return null
        const visible = role === 'todas' ? all : all.filter((contact) => contact.papel === role)
        if (!visible.length) return null
        const ordem = Math.min(...all.map((item) => item.ordemSetor || 9999))
        return { setor, ordem, contacts: visible, totalContacts: all.length }
      })
      .filter((item): item is NonNullable<typeof item> => Boolean(item))
      .sort((a, b) => a.ordem - b.ordem || a.setor.localeCompare(b.setor, 'pt-BR'))
  }, [sectorContacts, needle, role])

  const uniqueProfessionals = useMemo(() => new Set(contacts.map((item) => normalize(item.nome)).filter(Boolean)).size, [contacts])
  const visibleContacts = sectors.reduce((sum, sector) => sum + sector.contacts.length, 0) + visibleManagers.length

  async function handleCopy(label: string, value: string) {
    const ok = await copyText(value)
    setMessage(ok ? `${label} copiado.` : `Não foi possível copiar ${label.toLocaleLowerCase('pt-BR')}.`)
    window.setTimeout(() => setMessage(''), 1800)
  }

  return (
    <section className="central-contact-tool">
      <div className="tool-heading">
        <div>
          <p className="eyebrow">CONSULTA OPERACIONAL</p>
          <h2>Acionamentos Central de Serviços</h2>
          <p className="muted">Localize gerência, setor, responsabilidade e profissionais responsáveis. A busca por um contato mantém o setor completo para facilitar o acionamento.</p>
        </div>
        <span className={`status-chip ${source === 'cache' ? 'warning' : ''}`}>{source === 'cache' ? 'Contingência' : 'Base publicada'}</span>
      </div>

      <section className="central-toolbar-card">
        <div className="central-search-grid">
          <label>
            Buscar setor, nome, responsabilidade, e-mail ou telefone
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Ex.: manutenção, gerente, coordenador, nome, telefone..."
            />
          </label>
          <label>
            Responsabilidade
            <select value={role} onChange={(event) => setRole(event.target.value)}>
              <option value="todas">Todas</option>
              {roles.map((item) => <option value={item} key={item}>{item}</option>)}
            </select>
          </label>
          <button className="secondary-button" type="button" onClick={() => { setQuery(''); setRole('todas') }}>Limpar filtros</button>
        </div>

        <div className="central-metrics">
          <article><strong>{new Set(sectorContacts.map((item) => item.setor)).size}</strong><span>setores</span></article>
          <article><strong>{uniqueProfessionals}</strong><span>profissionais únicos</span></article>
          <article><strong>{visibleContacts}</strong><span>vínculos exibidos</span></article>
          <article><strong>{contacts.length}</strong><span>vínculos na base</span></article>
        </div>
        {source === 'cache' && savedAt && <p className="central-cache-note">Última sincronização local: {new Date(savedAt).toLocaleString('pt-BR')}</p>}
        {message && <div className="tool-status ok central-copy-message">{message}</div>}
      </section>

      {!loading && !error && visibleManagers.length > 0 && (
        <section className="central-management-card">
          <div className="central-management-heading">
            <div>
              <p className="eyebrow">GERÊNCIA DA CENTRAL DE SERVIÇOS</p>
              <h3>{visibleManagers[0].nome}</h3>
              <p>Responsabilidade transversal à Central de Serviços. Não é contabilizado como Coordenador de setor.</p>
            </div>
            <span>Gerência</span>
          </div>
          <div className="central-management-contacts">
            {visibleManagers[0].email && (
              <div className="central-contact-line">
                <a href={`mailto:${visibleManagers[0].email}`}>{visibleManagers[0].email}</a>
                <button type="button" onClick={() => void handleCopy('E-mail', visibleManagers[0].email)}>Copiar</button>
              </div>
            )}
            {visibleManagers[0].telefone && (
              <div className="central-contact-line">
                <a href={phoneHref(visibleManagers[0].telefone)}>{visibleManagers[0].telefone}</a>
                <button type="button" onClick={() => void handleCopy('Telefone', visibleManagers[0].telefone)}>Copiar</button>
              </div>
            )}
          </div>
          <div className="central-management-note">O registro ACS-040 ainda precisa ter o papel corrigido na base oficial. A Operação já trata Daniel corretamente como Gerência para evitar orientação indevida.</div>
        </section>
      )}

      {loading && <div className="empty-state">Carregando acionamentos...</div>}
      {error && <div className="notice error">{error}</div>}

      {!loading && !error && sectors.length === 0 && visibleManagers.length === 0 && (
        <div className="empty-state">Nenhum acionamento encontrado com os filtros informados.</div>
      )}

      {!loading && !error && (
        <div className="central-sector-list">
          {sectors.map((sector) => {
            const groupedRoles = (role === 'todas' ? SECTOR_ROLE_ORDER : [role])
              .map((roleName) => ({ role: roleName, contacts: sector.contacts.filter((item) => item.papel === roleName) }))

            const knownRoles = new Set(ROLE_ORDER)
            const otherRoles = Array.from(new Set(sector.contacts.map((item) => item.papel).filter((item) => !knownRoles.has(item))))
            otherRoles.forEach((roleName) => groupedRoles.push({ role: roleName, contacts: sector.contacts.filter((item) => item.papel === roleName) }))

            return (
              <article className="central-sector-card" key={sector.setor}>
                <header>
                  <div>
                    <p className="eyebrow">CENTRAL DE SERVIÇOS</p>
                    <h3>{sector.setor}</h3>
                  </div>
                  <div className="central-sector-badges">
                    <span>{sector.contacts.length} exibido(s)</span>
                    {sector.contacts.length !== sector.totalContacts && <span>{sector.totalContacts} no setor</span>}
                  </div>
                </header>

                <div className="central-role-grid">
                  {groupedRoles.map((group) => (
                    <section className={`central-role-block ${group.contacts.length === 0 ? 'is-empty' : ''}`} key={group.role}>
                      <div className="central-role-head"><strong>{group.role}</strong><span>{group.contacts.length}</span></div>
                      {group.contacts.length === 0 ? (
                        <div className="central-role-empty">
                          <strong>{group.role} não informado na base</strong>
                          <span>Não preencher por suposição. Atualize pelo Painel Administrativo quando houver confirmação.</span>
                        </div>
                      ) : (
                        <div className="central-people-list">
                          {group.contacts.map((contact) => (
                            <article className="central-person" key={contact.id}>
                              <strong>{contact.nome}</strong>
                              {contact.email && (
                                <div className="central-contact-line">
                                  <a href={`mailto:${contact.email}`}>{contact.email}</a>
                                  <button type="button" onClick={() => void handleCopy('E-mail', contact.email)}>Copiar</button>
                                </div>
                              )}
                              {contact.telefone && (
                                <div className="central-contact-line">
                                  <a href={phoneHref(contact.telefone)}>{contact.telefone}</a>
                                  <button type="button" onClick={() => void handleCopy('Telefone', contact.telefone)}>Copiar</button>
                                </div>
                              )}
                            </article>
                          ))}
                        </div>
                      )}
                    </section>
                  ))}
                </div>
              </article>
            )
          })}
        </div>
      )}
    </section>
  )
}
